import { inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import { ElasticGlobalSearchResponse, GlobalSearchParams, SearchResults } from 'app/pages/search-result/models/search-result.model';
import { LanguageService } from 'app/shared/services/language.service';
import { map, Observable, forkJoin, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { API_MAP } from '../helpers/api.map';
import { DataService } from '../../shared/services/data.service';
import { SEARCH_CONSTANTS } from '../constants/search.constants';
import { Router } from '@angular/router';
import { NavigationStateService } from './navigation-state-service';

export interface SearchSortConfig {
  sortByScore: boolean;
  fields: string[];
}

export interface SearchGenericResults<T> {
  total: number;
  values: T[];
}

@Injectable({
  providedIn: 'root'
})
export class ElasticsearchService {
  readonly dataService = inject(DataService);
  readonly currentLanguage = inject(LanguageService).currentLanguage;
  private readonly injector = inject(Injector);
  private router = inject(Router);

  query: any = this.createBaseQueryWithBoosts();
  constructor(private navigationStateService: NavigationStateService) {}

  initQuery(applyTypeKeywordBoosts: boolean = false, isAutoComplete: boolean = false): this {
    this.query = this.createBaseQueryWithBoosts(applyTypeKeywordBoosts, isAutoComplete);
    (this.query as any)._applyTypeKeywordBoosts = applyTypeKeywordBoosts;
    return this;
  }

  executeSearch<T>(
    indexes: string[],
    isAutoComplete?: boolean,
    sortConfig?: SearchSortConfig,
    skipLoader?: boolean,
    totalResult?: number,
  ): Observable<SearchGenericResults<T>> {
    const queryClone = structuredClone(this.query);

    if ((queryClone as any)._applyTypeKeywordBoosts) {
      this.appendFunctionScoreQuery(queryClone, isAutoComplete);
    } else {
      this.appendSearchQuery(queryClone, isAutoComplete);
    }

    delete (queryClone as any)._applyTypeKeywordBoosts;
    delete (queryClone as any)._searchTerm;
    delete (queryClone as any)._searchFields;

    const body = { indexes, query: queryClone };
    return this.dataService
    .post<T>(API_MAP.informationSearch, body, undefined, skipLoader)
    .pipe(map((data) => this.mapResults<T>(data, sortConfig, totalResult)));
  }

  searchAllFieldsByTerm(term?: string, fields?: string[]): this {
    if (!term) {
      return this;
    }

    const searchFields = fields && fields.length > 0 ? fields : ['*'];

    // Store the search term and fields for function_score query
    (this.query as any)._searchTerm = this.escapeElasticsearchSpecialChars(term);
    (this.query as any)._searchFields = searchFields;

    return this;
  }

  private escapeElasticsearchSpecialChars(text: string): string {
    return text.replace(/([+\-=&|><!(){}[\]^"~*?:\\/])/g, '\\$1');
  }

  searchAllFieldsByPartialTerm(term?: string, fields?: string[]): this {
    return this.searchAllFieldsByTerm(term, fields);
  }

  searchAll(from: number = 0, size: number = 1000): this {
    this.query.query = { match_all: {} };
    this.query.from = from;
    this.query.size = size;
    return this;
  }

  filterByField(field?: string, value?: string): this {
    if (field && value) {
      const boolQuery = this.getBoolQuery();
      boolQuery.must.push({
        match_phrase: {
          [field]: value
        }
      });
    }
    return this;
  }

  filterByExists(field?: string): this {
    if (!field) return this;

    const boolQuery = this.getBoolQuery();
    boolQuery.filter.push({
      exists: { field }
    });
    return this;
  }

  filterByFieldValues(field?: string, values?: string | (string | undefined)[]): this {
    if (!field || !values) return this;

    const valueArray = Array.isArray(values) ? values : [values];
    const validValues = valueArray.filter((v): v is string => !!v);

    if (validValues.length > 0) {
      const shouldArray = validValues.map((value) => ({
        match_phrase: {
          [field]: value
        }
      }));

      const boolQuery = this.getBoolQuery();
      boolQuery.must.push({
        bool: {
          should: shouldArray,
          minimum_should_match: 1
        }
      });
    }

    return this;
  }

  searchAllFieldsByValues(values?: string | (string | undefined)[], fields?: string[]): this {
    if (!values) return this;

    const valueArray = Array.isArray(values) ? values : [values];
    const validValues = valueArray.filter((v): v is string => !!v);
    const searchFields = fields && fields.length > 0 ? fields : ['*'];

    if (validValues.length > 0) {
      const boolQuery = this.getBoolQuery();
      boolQuery.filter.push({
        terms: {
          'typeText.keyword': validValues
        }
      });
    }

    return this;
  }

  filterByDateAfter(field: string, date: Date = new Date()): this {
    if (!field) return this;

    const isoDate = date.toISOString();
    const boolQuery = this.getBoolQuery();

    boolQuery.must.push({
      range: {
        [field]: {
          gte: isoDate
        }
      }
    });

    return this;
  }

  addPagination(skip?: number, take?: number): this {
    this.query = {
      ...this.query,
      from: skip ?? 0,
      size: take ?? 10000
    };
    return this;
  }

  private getBoolQuery(query: any = this.query): any {
    // Always work with the regular bool query during query building
    // The function_score will wrap it later in executeSearch
    if (!query.query.bool) {
      query.query.bool = {
        filter: [],
        must: [],
        should: [],
        minimum_should_match: 0
      };
    }
    return query.query.bool;
  }

  private createBaseQueryWithBoosts(applyTypeKeywordBoosts: boolean = false, isAutoComplete: boolean = false) {
    const baseQuery: any = {
      from: 0,
      size: 10000,
      query: {
        bool: {
          filter: [] as unknown[],
          must: [] as unknown[],
          should: [] as unknown[],
          minimum_should_match: 0
        }
      }
    };
    if (isAutoComplete)
      baseQuery._source = ["title", "link"];


    return baseQuery;
  }

  private appendSearchQuery(query: any, isAutoComplete?: boolean): void {
    const searchTerm = (query as any)._searchTerm;
    const searchFields = (query as any)._searchFields || ['*'];

    if (searchTerm) {
      const boolQuery = this.getBoolQuery(query);

      const shouldClauses: any[] = [];
      shouldClauses.push({
        wildcard: {
          "title.keyword": {
            value: '*' + searchTerm + '*',
            boost: SEARCH_CONSTANTS.WILDCARD_TITLE_BOOST,
          }
        }
      });
      shouldClauses.push({
        match_phrase: {
          title: {
            query: searchTerm,
            boost: SEARCH_CONSTANTS.TITLE_MATCH_BOOST,
            slop: 1
          }
        }
      });
      if (!isAutoComplete) {
        shouldClauses.push({
          multi_match: {
            query: searchTerm,
            type: "phrase",
            fields: searchFields,
            operator: 'and'
          }
        });
      }

      boolQuery.must.push({
        bool: {
          should: shouldClauses,
          minimum_should_match: 1
        }
      });
    }
  }
  getHospitalParamFromGlobalSearchPage(): string | null {
    const url = window.location.href;

    const parsedUrl = new URL(url);
    const hospitalParam = parsedUrl.searchParams.get('hospital'); // "he/tel-hashomer/"

    let lastPart: string | null = null;

    if (hospitalParam) {
      const decoded = decodeURIComponent(hospitalParam); // "he/tel-hashomer/"
      lastPart = decoded.split('/').filter(Boolean).pop() || null;
    }
    return lastPart;
  }
  private appendFunctionScoreQuery(query: any, isAutoComplete?: boolean): void {
    const typeKeywordBoosts = SEARCH_CONSTANTS.TYPE_KEYWORD_BOOSTS;
    const searchTerm = (query as any)._searchTerm;
    const searchFields = (query as any)._searchFields || ['*'];

    // Get existing must and filter clauses from the bool query
    const existingMust = query.query.bool?.must || [];
    const existingFilter = query.query.bool?.filter || [];

    // Build the bool query with match and multi_match
    const boolQuery: any = {
      bool: {
        must: [...existingMust]
      }
    };



    if (isAutoComplete) {
      const must: any = [];

      this.navigationStateService.hasVisitedHospitalLobby().subscribe((visited) => {
        if (visited) {
          let hospitalName = this.getHospitalParamFromGlobalSearchPage();
          if (!hospitalName){
            const parts = this.router.url.split('/');
            hospitalName = parts[1] == this.currentLanguage().prefix? parts[2]: parts[1];
          }
          if (hospitalName && !hospitalName.includes('?') && hospitalName != this.currentLanguage().prefix && !hospitalName.endsWith('/home')) {
            must.push({
              match_phrase: {
                hospital: hospitalName // שימוש בשם בית החולים רק אם קיים
              }
            });
          }
        }
      });

      const safeSearchTerm = searchTerm.trim().replace(/\s+/g, ' ');

      must.push({
        query_string: {
          default_field: "title",
          query: '*' + safeSearchTerm + '*',

        }
      });

      boolQuery.bool.must = must;
    }
    else {
      // Only add filter if it has items
      if (existingFilter.length > 0) {
        boolQuery.bool.filter = [...existingFilter];
      }

      if (searchTerm) {
        const shouldClauses = [
          {
            wildcard: {
              "title.keyword": {
                value: '*' + searchTerm + '*',
                boost: SEARCH_CONSTANTS.WILDCARD_TITLE_BOOST,
              }
            }
          },
          {
            match_phrase: {
              title: {
                query: searchTerm,
                boost: SEARCH_CONSTANTS.TITLE_MATCH_BOOST,
                slop: 1
              }
            }
          },
          {
            multi_match: {
              query: searchTerm,
              fields: searchFields,
              type: "phrase",
              operator: "and"
            }
          }
        ];


        boolQuery.bool.must.push({
          bool: {
            should: shouldClauses,
            minimum_should_match: 1
          }
        });
      }

      // Build functions from TYPE_KEYWORD_BOOSTS
      const functions = typeKeywordBoosts
        .filter((boostConfig) => boostConfig?.name)
        .map((boostConfig) => ({
          filter: {
            term: {
              'typeText.keyword': boostConfig.name.trim()
            }
          },
          weight: boostConfig.boost ?? 1
        }));
    }
    // Create function_score query
    query.query = {
      function_score: {
        query: boolQuery,
        // functions: functions,
        score_mode: 'sum',
        boost_mode: 'sum'
      }
    };

  }

  private mapResults<T>(data: any, sortConfig?: SearchSortConfig, totalResult?: number): SearchGenericResults<T> {
    let sorted: any[] = [];

    if (sortConfig) {
      sorted = this.sortResultsByConfig(data?.hits?.hits ?? [], sortConfig);
    } else {
      sorted = data?.hits?.hits ?? [];
    }

    const mappedValues = sorted.map((v: any) => ({
      ...(v._source as T),
      id: (v._source as { id?: string })?.id ?? v._id
    }));

    const limitedValues =
      totalResult && mappedValues.length > totalResult
        ? mappedValues.slice(0, totalResult)
        : mappedValues;

    return {
      values: limitedValues,
      total: limitedValues.length
    } as SearchGenericResults<T>;
  }

  private sortResultsByConfig(hits: any[], config: SearchSortConfig): any[] {
    return hits.sort((a: any, b: any) => {
      if (config.sortByScore) {
        const scoreDiff = (b._score ?? 0) - (a._score ?? 0);
        if (scoreDiff !== 0) return scoreDiff;
      }

      for (const field of config.fields) {
        const fieldDiff = this.compareFieldValues(a._source?.[field], b._source?.[field]);
        if (fieldDiff !== 0) return fieldDiff;
      }

      return 0;
    });
  }

  private compareFieldValues(valueA: unknown, valueB: unknown): number {
    if (valueA == null && valueB == null) return 0;
    if (valueA == null) return 1;
    if (valueB == null) return -1;

    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return valueA - valueB;
    }

    if (this.isValidDate(valueA) && this.isValidDate(valueB)) {
      return new Date(valueA as string).getTime() - new Date(valueB as string).getTime();
    }

    return String(valueA).localeCompare(String(valueB), this.currentLanguage().isoCode);
  }

  private isValidDate(value: any): boolean {
    if (typeof value !== 'string') return false;

    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;
    if (!isoDateRegex.test(value)) return false;

    const date = new Date(value);
    return !isNaN(date.getTime());
  }

  getAutocompleteSuggestions(
    term: string,
    indexName: string,
    fields?: string[],
    filters?: { [key: string]: any }
  ): Observable<SearchResults> {
    const queryBuilder = this.initQuery(undefined, true);

    if (filters) {
      if (filters['typeId']) {
        queryBuilder.filterByFieldValues('typeId', filters['typeId']);
      }

      if ('endDate' in filters) {
        queryBuilder.filterByDateAfter('endDate');
      }

      Object.keys(filters).forEach((key) => {
        if (key !== 'typeId' && key !== 'endDate' && filters[key] !== undefined && filters[key] !== null) {
          if (Array.isArray(filters[key])) {
            queryBuilder.filterByFieldValues(key, filters[key]);
          } else {
            queryBuilder.filterByField(key, filters[key].toString());
          }
        }
      });
    }

    const query = queryBuilder.searchAllFieldsByPartialTerm(term, fields).addPagination(0, 5);

    return query.executeSearch<ElasticGlobalSearchResponse>([`${this.currentLanguage()?.prefix}_${indexName}`], undefined, undefined, true);
  }

  getGlobalSearchIndexes(): string[] {
    const currentLanguagePrefix = this.currentLanguage()?.prefix || 'he';

    return [
      `${currentLanguagePrefix}_${environment.elasticsearch.indexes.doctor}`,
      `${currentLanguagePrefix}_${environment.elasticsearch.indexes.internalDepartment}`,
      `${currentLanguagePrefix}_${environment.elasticsearch.indexes.contentPage}`,
      `${currentLanguagePrefix}_${environment.elasticsearch.indexes.event}`,
      `${currentLanguagePrefix}_${environment.elasticsearch.indexes.generalContent}`
    ];
  }

  searchGlobalResults(
    params: GlobalSearchParams,
    fields: string[]
  ): Observable<SearchGenericResults<ElasticGlobalSearchResponse>> {
    const indexes = this.getGlobalSearchIndexes();

    if (indexes.length === 0) {
      return of({ total: 0, values: [] });
    }

    const searches = indexes.map((index) => {
      this.initQuery(true)
        .searchAllFieldsByPartialTerm(params.term, fields)
        .searchAllFieldsByValues(params.pageTypes, fields);

      if (params.hospital) {
        this.filterByField('hospital', params.hospital);
      }

      this.addPagination(params.skip, params.take);

      return this.executeSearch<ElasticGlobalSearchResponse>([index], false);
    });

    return forkJoin(searches).pipe(
      map((results) => {
        const combinedValues = results.flatMap((result) => result.values ?? []);
        const pageResults = combinedValues.filter(
          (item) => typeof item?.link === 'string' && item.link.trim().length > 0
        );
        const uniqueByLink = Array.from(
          new Map(pageResults.map((item) => [item.link, item])).values()
        );

        return {
          total: uniqueByLink.length,
          values: uniqueByLink.slice(0, params.take ?? 30)
        };
      })
    );
  }

  getMultiIndexAutocompleteSuggestions(
    term: string,
    fields?: string[],
    filters?: { [key: string]: any }
  ): Observable<SearchResults> {
    const query = this.initQuery(true, true).searchAllFieldsByTerm(term, fields);

    if (filters) {
      if (filters['typeId']) {
        query.filterByFieldValues('typeId', filters['typeId']);
      }

      if ('endDate' in filters) {
        query.filterByDateAfter('endDate');
      }

      Object.keys(filters).forEach((key) => {
        if (key !== 'typeId' && key !== 'endDate' && filters[key] !== undefined && filters[key] !== null) {
          if (Array.isArray(filters[key])) {
            query.filterByFieldValues(key, filters[key]);
          } else {
            query.filterByField(key, filters[key].toString());
          }
        }
      });
    }

    query.addPagination(0, 5);
    return query.executeSearch(this.getGlobalSearchIndexes(), true, undefined, true).pipe(
      map((result: SearchGenericResults<any>) => {
        return {
          total: result.total || 0,
          values: result.values?.slice(0, 5) || []
        } as SearchResults;
      })
    );

  }
}
